import { observable, computed, action, makeObservable } from 'mobx';
import {DomainObject} from 'shared/store';
import Collaborator from './Collaborator';
import Question from 'main/stores/recruiting/Question';
import _ from 'lodash';
import {FunnelStage} from 'main/stores/recruiting/FunnelStage';
import striptags from 'striptags';
import Location from 'main/stores/locations/Location';
import Department from 'main/stores/departments/Department';
import {calendarDate} from 'shared/tools';
import PositionRule from 'main/stores/recruiting/PositionRule';
import AiScreenerPrompt from 'main/stores/recruiting/AiScreenerPrompt';

class Position extends DomainObject {
  title = '';
  currentStep = 1;
  employmentType = '';
  status = '';
  location = new Location();
  locationString = '';
  remoteString = '';
  department = new Department();
  candidateCount;
  createdAt;
  contentSection = null;
  hiringCollaborators = [];
  funnelStages = [];
  deleted;
  sendEmailToApplicants = true;
  sendDisqualifiedEmailToApplicants = false;
  allowReferrals = false;
  questions = [];
  rules = [];
  locale;
  bilingual = false;
  requireResume;
  minimumPayRate;
  maximumPayRate;
  payRateCurrency;
  payRateUnit;
  payRateFormat;
  locationType;
  currentPrompt;
  activeCandidateCount;

  _relations = {
    location: Location,
    department: Department,
    hiringCollaborators: [Collaborator],
    funnelStages: [FunnelStage],
    questions: [Question],
    rules: [PositionRule],
    currentPrompt: AiScreenerPrompt
  };

  constructor(other) {
    super();

    makeObservable(this, {
      title: observable,
      currentStep: observable,
      employmentType: observable,
      status: observable,
      location: observable,
      locationString: observable,
      remoteString: observable,
      department: observable,
      candidateCount: observable,
      createdAt: observable,
      contentSection: observable,
      hiringCollaborators: observable,
      funnelStages: observable,
      deleted: observable,
      sendEmailToApplicants: observable,
      sendDisqualifiedEmailToApplicants: observable,
      allowReferrals: observable,
      questions: observable,
      rules: observable,
      locale: observable,
      bilingual: observable,
      requireResume: observable,
      minimumPayRate: observable,
      maximumPayRate: observable,
      payRateCurrency: observable,
      payRateUnit: observable,
      payRateFormat: observable,
      locationType: observable,
      currentPrompt: observable,
      activeCandidateCount: observable,
      stages: computed,
      finalStages: computed,
      isDraft: computed,
      isPublished: computed,
      isInternal: computed,
      isClosed: computed,
      priority: computed,
      description: computed,
      departmentView: computed,
      selectedCollaborators: computed,
      selectedCollaboratorUsers: computed,
      truncatedDescription: computed,
      customFunnelStages: computed,
      showViewPosting: computed,
      viewPostingLink: computed,
      sortedQuestions: computed,
      createdDate: computed,
      directoryUrl: computed,
      addCollaborator: action,
      removeCollaborator: action,
      indeedLocationRequiredFields: computed
    });

    this.merge(other);
  }

  get stages() {
    return _.chain(this.funnelStages)
      .reject(s => ['hired', 'offer'].includes(s.stageType))
      .reject('_destroy')
      .orderBy('order')
      .value();
  }

  get finalStages() {
    return _.filter(this.funnelStages, s => ['hired', 'offer'].includes(s.stageType));
  }

  get isDraft() {
    return this.status === 'draft';
  }

  get isPublished() {
    return this.status === 'published';
  }

  get isInternal() {
    return this.status === 'internal';
  }

  get isClosed() {
    return this.status === 'closed';
  }

  get priority() {
    if (this.isClosed) return 0;
    if (this.isInternal) return 1;
    return 2;
  }

  get description() {
    return _.get(this.contentSection, 'richText', '');
  }

  get departmentView() {
    return _.get(this.department, 'name', '');
  }

  get selectedCollaborators() {
    return _.reject(this.hiringCollaborators, '_destroy');
  }

  get selectedCollaboratorUsers() {
    return _.map(this.selectedCollaborators, 'user');
  }

  get truncatedDescription() {
    const plainText = striptags(this.contentSection.richText);
    return _.chain(plainText)
      .trim('&nbsp;')
      .truncate({length: 60})
      .value();
  }

  get customFunnelStages() {
    return _.orderBy(_.filter(this.funnelStages, {stageType: 'custom'}), 'order');
  }

  get showViewPosting() {
    return this.hasLink('posting');
  }

  get viewPostingLink() {
    return this.link('posting');
  }

  get sortedQuestions() {
    return _.sortBy(
      this.questions,
      'order'
    );
  }

  get createdDate() {
    return calendarDate(this.createdAt);
  }

  get directoryUrl() {
    return this.isDraft ? `/positions/${this.id}` : `/positions/${this.id}/funnel/`;
  }

  addCollaborator(collaborator) {
    const collaboratorToRemove = _.find(this.hiringCollaborators, {clientId: collaborator.clientId});
    if (collaboratorToRemove) {
      this.hiringCollaborators.remove(collaboratorToRemove);
    }
    this.hiringCollaborators.push(new Collaborator(collaborator));
  }

  removeCollaborator(collaborator) {
    const collaboratorToRemove = _.find(this.hiringCollaborators, {clientId: collaborator.clientId});
    if (collaboratorToRemove.id) {
      collaboratorToRemove._destroy = true;
    } else {
      this.hiringCollaborators.remove(collaboratorToRemove);
    }
  }

  get indeedLocationRequiredFields() {
    const requiredFields = [['address', 'postalCode'], ['city'], ['regionCode'], ['countryCode']];
    const locationTypes = [['on_site', 'hybrid'], ['remote_citywide'], ['remote_regionwide'], ['remote_nationwide'], ['fully_remote']];

    const typeIndex = _.findIndex(locationTypes, options => options.includes(this.locationType));

    return _.flatten(requiredFields.slice(typeIndex));
  }
}

export default Position;
